import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useQuery } from "react-query";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import dayjs from "dayjs";
import { CheckmarkCircle02 } from "@/v2/assets/icons/CheckmarkCircle02";
import { Alert02Icon } from "@/v2/assets/icons/Alert02Icon";
import { AlertCircleIcon } from "@/v2/assets/icons/AlertCircleIcon";

interface LastResultButtonProps {
  simulatorId: string;
}

export function LastResultButton({ simulatorId }: LastResultButtonProps) {
  const navigate = useNavigate();
  const { getRegressionSetsBySimulatorId } = useRegressionApi();

  const { data: regressionSets } = useQuery({
    queryKey: ["regression-sets", simulatorId],
    queryFn: () => getRegressionSetsBySimulatorId(simulatorId),
    enabled: !!simulatorId,
  });

  const lastResult = useMemo(() => {
    if (!regressionSets || regressionSets.length === 0) {
      return null;
    }

    const autoSet = regressionSets.find((set) => set.frequency);
    const setToUse = autoSet || regressionSets[0];
    return setToUse.mostRecentTest;
  }, [regressionSets]);

  const resultColor = useMemo(() => {
    if (!lastResult?.avgAccuracy) {
      return "gray";
    }
    if (lastResult.avgAccuracy >= 0.8) {
      return "green";
    }
    if (lastResult.avgAccuracy >= 0.6) {
      return "yellow";
    }
    return "red";
  }, [lastResult]);

  const resultIcon = useMemo(() => {
    if (!lastResult?.avgAccuracy) {
      return null;
    }
    if (lastResult.avgAccuracy >= 0.8) {
      return <CheckmarkCircle02 size={16} fill="green" />;
    }
    if (lastResult.avgAccuracy >= 0.6) {
      return <Alert02Icon size={16} fill="yellow" />;
    }
    return <AlertCircleIcon size={16} fill="red" />;
  }, [lastResult]);

  const formattedRunDate = useMemo(() => {
    if (!lastResult?.runTime) {
      return null;
    }
    return dayjs(lastResult.runTime).format("MMM D, YYYY");
  }, [lastResult]);

  const handleClick = useCallback(() => {
    const runDate = lastResult?.runTime
      ? new Date(lastResult.runTime).toISOString()
      : undefined;
    const params = new URLSearchParams();
    if (runDate) {
      params.set("runDate", runDate);
    }
    navigate(`/training-center/${simulatorId}?${params.toString()}`);
  }, [navigate, simulatorId, lastResult]);

  if (!regressionSets || regressionSets.length === 0) {
    return (
      <Button variant="ghost" color="gray" onClick={handleClick} size="2">
        <Flex align="center" gap="2">
          <Text size="2">No Accuracy Check</Text>
        </Flex>
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      color="gray"
      highContrast
      onClick={handleClick}
      size="2"
    >
      <Flex align="center" gap="2">
        {resultIcon}
        <Text size="2">
          {lastResult?.avgAccuracy
            ? RatioFormatter({ value: lastResult.avgAccuracy })
            : "No Result"}
        </Text>
        {formattedRunDate && (
          <Text size="1" color="gray">
            {formattedRunDate}
          </Text>
        )}
      </Flex>
    </Button>
  );
}
